import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import DataTable from 'react-data-table-component';
import {getReport, ReportFilterValues, ReportPayload} from '@app/services/reports';
import {getAllActiveFranchise} from '@app/services/franchise';
import KpiStrip from './components/KpiStrip';
import ReportFilters, {FranchiseOption} from './components/ReportFilters';
import {
  ChartCard,
  ReportBarChart,
  ReportHorizontalBarChart
} from './components/ReportChart';
import useReportExport from './hooks/useReportExport';
import {reportTableStyles} from './components/tableStyles';
import {monthYear} from './components/labels';
import {findReportBySlug} from './reportCatalogue';

/**
 * PRT3 — Printing Threshold Breaches.
 *
 * The breach rule is the live one from models/franchise-printing-threshold.js,
 * not a second implementation — otherwise the report would contradict the
 * warnings users already see at print time.
 *
 * Coverage is shown next to breaches on purpose: a franchise with no configured
 * threshold cannot breach, so an empty breach list is not the same as
 * compliance. On stage only half the printing franchises have a limit at all.
 */

const SLUG = 'threshold-breaches';
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({length: 5}, (_, i) => CURRENT_YEAR - i);

const number = (v: any) => new Intl.NumberFormat('en-GB').format(Number(v) || 0);

const ThresholdBreaches = () => {
  const definition = findReportBySlug(SLUG);

  const [payload, setPayload] = useState<ReportPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [franchises, setFranchises] = useState<FranchiseOption[]>([]);

  const [filters, setFilters] = useState<ReportFilterValues>({
    mode: 'year',
    year: CURRENT_YEAR,
    period: 'full',
    page: 1,
    limit: 50
  });
  const [applied, setApplied] = useState<ReportFilterValues>(filters);

  const {exportToExcel, exporting} = useReportExport('Threshold Breaches');

  const load = useCallback(async (active: ReportFilterValues) => {
    setLoading(true);
    setError(null);
    const response = await getReport(SLUG, active);
    if (response && response.success) {
      setPayload(response);
    } else {
      setPayload(null);
      setError(response?.message || 'The report could not be loaded.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(applied);
  }, [applied, load]);

  useEffect(() => {
    (async () => {
      const response = await getAllActiveFranchise();
      if (response?.success === true && Array.isArray(response.collections)) {
        setFranchises(
          response.collections.map((f: any) => ({value: f.id, label: f.name}))
        );
      }
    })();
  }, []);

  const collections = payload?.collections;
  const byMonth = collections?.series?.by_month || [];
  const byFranchise = collections?.series?.by_franchise || [];
  const detail = collections?.detail;

  const columns = [
    {name: 'Franchise', selector: (row: any) => row.franchise || '---', wrap: true},
    {name: 'Student', selector: (row: any) => row.student || '---', wrap: true},
    {name: 'Subject', selector: (row: any) => row.subject || '---', width: '130px'},
    {name: 'Month', selector: (row: any) => monthYear(row.month) || '---', width: '140px'},
    {name: 'Printed', width: '100px', right: true, selector: (row: any) => number(row.prints)},
    {name: 'Limit', width: '90px', right: true, selector: (row: any) => number(row.threshold)},
    {
      name: 'Over by',
      width: '110px',
      right: true,
      cell: (row: any) => (
        <span className="text-danger font-weight-bold">{number(row.over_by)}</span>
      )
    },
    {
      name: 'Limit from',
      width: '150px',
      cell: (row: any) => (
        <span
          className={`badge badge-${row.threshold_source === 'franchise' ? 'info' : 'secondary'}`}
          title={
            row.threshold_source === 'franchise'
              ? "The franchise's own configured limit"
              : 'Subject default — this month predates the franchise limit being set'
          }>
          {row.threshold_source === 'franchise' ? 'Franchise' : 'Subject default'}
        </span>
      )
    }
  ];

  return (
    <div>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-7">
              <h1>{definition?.name || 'Threshold Breaches'}</h1>
              <p className="text-muted mb-0">{definition?.question}</p>
            </div>
            <div className="col-sm-5">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item"><Link to="/reports">Reports</Link></li>
                <li className="breadcrumb-item active">Threshold Breaches</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <ReportFilters
            values={filters}
            onChange={setFilters}
            onApply={() => setApplied({...filters, limit: applied.limit, page: 1})}
            onReset={(defaults) => {
              setFilters(defaults);
              setApplied(defaults);
            }}
            onExport={() => exportToExcel(payload, SLUG, applied)}
            exporting={exporting}
            franchises={franchises}
            years={YEARS}
            intervalLabel={collections?.filters_applied?.interval_label}
            show={{franchise: true, subject: true, student: true}}
            disabled={loading}
          />

          {error && (
            <div className="alert alert-warning">
              <i className="fas fa-exclamation-triangle mr-2" />
              {error}
            </div>
          )}

          <KpiStrip kpis={collections?.kpis || []} loading={loading} />

          <div className="alert alert-light border">
            <i className="fas fa-info-circle mr-2 text-info" />
            A franchise with no configured limit cannot appear here — nothing is
            being checked for it. Read the coverage figure alongside the breach
            count, not instead of it.
          </div>

          <div className="row">
            <div className="col-lg-6">
              <ChartCard
                title="Breaches by month"
                subtitle="Counted per student, subject and month"
                loading={loading}
                isEmpty={!byMonth.length}>
                <ReportBarChart
                  data={byMonth}
                  xKey="month"
                  yKey="breaches"
                  currency={false}
                  colour="#B04A5A"
                />
              </ChartCard>
            </div>
            <div className="col-lg-6">
              <ChartCard
                title="Breaches by franchise"
                subtitle="Only franchises that have a limit configured can appear"
                loading={loading}
                isEmpty={!byFranchise.length}>
                <ReportHorizontalBarChart
                  data={byFranchise}
                  labelKey="franchise"
                  valueKey="breaches"
                  currency={false}
                  labelWidth={170}
                  colour="#C9873A"
                />
              </ChartCard>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h3 className="card-title text-capitalize">Breaches, worst overshoot first</h3>
            </div>
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                customStyles={reportTableStyles}
                data={detail?.rows || []}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={detail?.total || 0}
                paginationDefaultPage={applied.page || 1}
                paginationPerPage={applied.limit || 50}
                paginationRowsPerPageOptions={[25, 50, 100]}
                onChangePage={(page) => setApplied({...applied, page})}
                onChangeRowsPerPage={(limit, page) =>
                  setApplied({...applied, limit, page})
                }
                noDataComponent={
                  <div className="text-muted py-4">
                    No breaches in this period — among the franchises that have a limit set.
                  </div>
                }
                highlightOnHover
                dense
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ThresholdBreaches;
